import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  followPooler,
  getFollowedPoolerIds,
  unfollowPooler,
} from '../../lib/supabase';

interface PoolerFollowButtonProps {
  leagueId?: number | null;
  currentUserId?: number | null;
  targetUserId?: number | null;
  initialFollowed?: boolean;
  onChanged?: (targetUserId: number, isFollowed: boolean) => void;
  size?: 'sm' | 'md';
}

export function PoolerFollowButton({
  leagueId,
  currentUserId,
  targetUserId,
  initialFollowed,
  onChanged,
  size = 'sm',
}: PoolerFollowButtonProps) {
  const [isFollowed, setIsFollowed] = useState(Boolean(initialFollowed));
  const [loading, setLoading] = useState(false);

  const normalizedLeagueId = leagueId ? Number(leagueId) : null;
  const normalizedCurrentUserId = currentUserId ? Number(currentUserId) : null;
  const normalizedTargetUserId = targetUserId ? Number(targetUserId) : null;

  useEffect(() => {
    setIsFollowed(Boolean(initialFollowed));
  }, [initialFollowed]);

  useEffect(() => {
    if (initialFollowed !== undefined) return;
    if (!normalizedLeagueId || !normalizedCurrentUserId || !normalizedTargetUserId) return;
    if (normalizedCurrentUserId === normalizedTargetUserId) return;

    let cancelled = false;

    async function loadFollowState() {
      const result = await getFollowedPoolerIds(normalizedLeagueId, normalizedCurrentUserId);
      if (cancelled || result.error) return;
      setIsFollowed(result.data.includes(normalizedTargetUserId));
    }

    loadFollowState();

    return () => {
      cancelled = true;
    };
  }, [initialFollowed, normalizedLeagueId, normalizedCurrentUserId, normalizedTargetUserId]);

  if (!normalizedLeagueId || !normalizedCurrentUserId || !normalizedTargetUserId) {
    return null;
  }

  if (normalizedCurrentUserId === normalizedTargetUserId) {
    return null;
  }

  const handleClick = async () => {
    if (loading) return;

    try {
      setLoading(true);
      const result = isFollowed
        ? await unfollowPooler(normalizedLeagueId, normalizedCurrentUserId, normalizedTargetUserId)
        : await followPooler(normalizedLeagueId, normalizedCurrentUserId, normalizedTargetUserId);

      if (!result.success) {
        throw new Error(result.error?.message || 'Action impossible');
      }

      const nextFollowed = !isFollowed;
      setIsFollowed(nextFollowed);
      onChanged?.(normalizedTargetUserId, nextFollowed);
      toast.success(nextFollowed ? 'Pooler suivi' : 'Pooler retiré des suivis');
    } catch (error) {
      console.error('Pooler follow action failed:', error);
      toast.error('Impossible de modifier le suivi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      className={`btn ${isFollowed ? 'btn-primary' : 'btn-outline-primary'} ${size === 'sm' ? 'btn-sm' : ''}`}
      onClick={handleClick}
      disabled={loading}
      aria-pressed={isFollowed}
    >
      {loading ? '...' : isFollowed ? 'Suivi' : 'Suivre'}
    </button>
  );
}